/* eslint-disable import/order */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FormInstance, Input, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { find, get } from 'lodash';
import produce from 'immer';
import styled from 'styled-components';

import {
  Checkbox,
  DateInput,
  Form,
  DocumentInput,
  PhoneNumberFormItem,
  Select,
  EmiratesIdInput,
  NationalitySelect,
  CountryInput,
} from '~/components';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import Section from '~/layouts/Section ';
import { Person } from '~/types/Person';
import createUid from '~/utils/createUid';
import theme from '~/constants/theme';
import { AuthorizedRepresentativeSendInvitationPayload } from '~/types/AuthorizedRepresentativeSendInvitationPayload';
import { getDecodedJwt, isAgent, isUaePassFromUAE } from '~/utils/getDecodedJwt';
import i18n from '~/i18n';
import { useHandbook } from '~/controllers/handbook';
import CustomFields from '~/components/CustomFields';
import { NamePath } from '~/utils/buildNamePath';
import features from '~/constants/features';
import { validatePassportNumber } from '~/utils/passportValidation';
import { useSaveDraft } from '~/controllers/draft';
import { getUpdatedContact } from '~/utils/contacts';

import AuthorizedRepresentativeSendInvitation from './AuthorizedRepresentativeSendInvitation';
import EmailFormItem from '../../inputs/EmailFormItem';

interface Props {
  form: FormInstance;
  name: string | (string | number)[];
  defaultIsAuthorizedSignatory?: boolean;
  withFiles?: boolean;
  withProof?: boolean;
  withAgentSendInvitation?: boolean;
  withUaePass?: boolean;
  withCustomFields?: boolean;
  isEmailAndPhoneDisabled?: boolean;
}

const AuthorizedRepresentativeFormItems: FC<Props> = (props) => {
  const {
    name,
    form,
    defaultIsAuthorizedSignatory,
    withFiles = true,
    withProof = true,
    withAgentSendInvitation = false,
    withUaePass = false,
    withCustomFields = true,
    isEmailAndPhoneDisabled = false,
  } = props;
  const { t } = useTranslation('fillApplication');
  const fromUAE = isUaePassFromUAE();
  const [isEmailDisabled, setEmailDisabled] = useState<boolean>();
  const [isPhoneDisabled, setPhoneDisabled] = useState<boolean>();

  useEffect(() => {
    setTimeout(() => {
      const formValues = form.getFieldsValue();
      const initialPerson: Person = get(formValues, name) || {};
      const updatedContact = getUpdatedContact(initialPerson, 'LATEST').then((data) => {
        const patchedPerson = produce<Person>(initialPerson, (draft) => {
          if (!draft.uid) draft.uid = createUid();
          if (draft.isAuthorizedSignatory === undefined) {
            draft.isAuthorizedSignatory = !!defaultIsAuthorizedSignatory;
          }
          if (draft.isPep === undefined) draft.isPep = false;
        });
        form.setFields([{ name, value: { ...patchedPerson, ...data } }]);
        setEmailDisabled(isEmailAndPhoneDisabled && !!form.getFieldValue(n(['email'])));
        setPhoneDisabled(isEmailAndPhoneDisabled && !!form.getFieldValue(n(['phoneNumber'])));
      });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const n = useCallback((namePath: NamePath) => [name].flat().concat([namePath].flat()), [name]);

  const handbook = useHandbook();
  const decodedToken = getDecodedJwt();
  const uaePassNationality = handbook.data?.handbooks
    ?.find((item) => item.key === 'nationality')
    ?.values?.find(
      (item) =>
        item?.additionalParams?.uaepass &&
        decodedToken?.uaepass_nationality_en &&
        item?.additionalParams?.uaepass === decodedToken?.uaepass_nationality_en,
    )?.apiValue;

  useEffect(() => {
    setTimeout(() => {
      if (withUaePass && uaePassNationality) {
        form.setFieldsValue(
          produce(form.getFieldsValue(), (draft: any) => {
            draft.authorizedRepresentative[0].nationality = uaePassNationality;
          }),
        );
      }
    }, 0);
  }, [uaePassNationality, form, withUaePass]);
  // const draftMutation = useSaveDraft();
  // const documentTypeValue = form.getFieldValue([...name, proof, 'documentType']);

  // useEffect(() => {
  //   if (documentTypeValue === getProofOptions()[0]?.value) {
  //     form.setFieldValue([...name, proof, 'files'], []);
  //     const formValues = form.getFieldsValue();
  //     draftMutation.mutate({
  //       stepData: {
  //         ...formValues,
  //       },
  //     });
  //   }
  // }, [documentTypeValue]);

  const initialValues = find(
    form.getFieldValue('authorizedRepresentative'),
    (authorizedRepresentative) => authorizedRepresentative,
  );
  return (
    <Container>
      <Form.Item name={n(['uid'])} hidden />
      <Form.Item
        hidden
        label={t('contactId')}
        name={n(['contactId'])}
        initialValue={get(initialValues, n(['contactId'])) || createUid()}
      />
      <Form.Item
        hidden
        label={t('userRoles')}
        name={n(['userRoles'])}
        initialValue={get(initialValues, n(['userRoles'])) || ['authorizedRepresentative']}
      />
      <Form.Item hidden name={n(['salesforceFields'])} initialValue={get(initialValues, n(['salesforceFields']))} />
      <Form.Item hidden name={n(['salesForceUser'])} initialValue={get(initialValues, n(['salesForceUser']))} />
      <Section>
        <Typography.Title level={4}>{t('Personal details of the Authorized Signatory').toUpperCase()}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item label={t('First name')} name={n(authorizedRepresentativeCommonNames.firstName)}>
            <Input />
          </Form.Item>
          <Form.Item label={t('Last name')} name={n(authorizedRepresentativeCommonNames.lastName)}>
            <Input />
          </Form.Item>
          <Form.Item label={t('Date of birth')} name={n(['dateOfBirth'])}>
            <DateInput disableDateRule="date <= today" />
          </Form.Item>
          <Form.Item
            label={t('Nationality')}
            name={n(['nationality'])}
            initialValue={withUaePass ? uaePassNationality : undefined}
          >
            <NationalitySelect />
          </Form.Item>
        </InputsGroupLayout>
        <Form.Item name={n(['isPep'])} valuePropName="checked" noStyle>
          <Checkbox>{t('Shareholder is a Politically Exposed Person (PEP)')}</Checkbox>
        </Form.Item>
        {defaultIsAuthorizedSignatory ? (
          <Form.Item name={n(['isAuthorizedSignatory'])} valuePropName="checked" noStyle />
        ) : (
          <Form.Item name={n(['isAuthorizedSignatory'])} valuePropName="checked">
            <Checkbox>{t('This person is legally authorized signatory')}</Checkbox>
          </Form.Item>
        )}
      </Section>

      <Section>
        <Typography.Title level={4}>{t('Passport').toUpperCase()}</Typography.Title>

        {withFiles && (
          <>
            <Typography.Paragraph>
              <Trans
                t={t}
                defaults="Please upload copy of passport main photo page to populate the details in the fields below."
              />
            </Typography.Paragraph>
            <Form.Item name={n([passport, 'files'])} forceDisabled={withUaePass && fromUAE}>
              <DocumentInput name="photos" variant="default" />
            </Form.Item>
          </>
        )}

        {/* {withFiles && (
          <>
            <Typography.Paragraph>
              <Trans
                t={t}
                defaults="Please upload copy of the visa page to populate the details in the fields below."
              />
            </Typography.Paragraph>
            <Form.Item name={n(['visa', 'files'])} forceDisabled={withUaePass && !fromUAE}>
              <DocumentInput name="photos" variant="default" />
            </Form.Item>
          </>
        )} */}

        <Typography.Title level={5}>{t('Passport details:')}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item
            label={t('Document number')}
            name={n([passport, 'documentNumber'])}
            forceDisabled={withUaePass && fromUAE}
            validateFirst
            // rules={[{ required: true }, { validator: validatePassportNumber }]}
          >
            <Input />
          </Form.Item>
          {/* <Form.Item
            label={t('Nationality')}
            name={n([passport, 'Nationality'])}
            forceDisabled={withUaePass && fromUAE}
          >
            <NationalitySelect />
          </Form.Item> */}
          <Form.Item
            label={t('Passport expiry date')}
            name={n([passport, 'expiryDate'])}
            forceDisabled={withUaePass && fromUAE}
          >
            <DateInput disableDateRule="date > today" />
          </Form.Item>
        </InputsGroupLayout>
      </Section>

      <Section formItemLast>
        <Typography.Title level={4}>{t('Emirates ID').toUpperCase()}</Typography.Title>

        {withFiles && (
          <>
            <Typography.Paragraph>
              <Trans t={t} defaults="Please upload copy of both the front and back of the Emirates ID." />
            </Typography.Paragraph>
            <Form.Item name={n([emiratesId, 'files'])} forceDisabled={withUaePass}>
              <DocumentInput name="photos" variant="default" maxCount={2} />
            </Form.Item>
          </>
        )}

        <Typography.Title level={5}>{t('Emirates ID details:')}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item
            label={t('Emirates ID number')}
            name={n([emiratesId, 'documentNumber'])}
            forceDisabled={withUaePass}
          >
            <EmiratesIdInput />
          </Form.Item>
          <Form.Item
            label={t('Emirates ID expiry date')}
            name={n([emiratesId, 'expiryDate'])}
            forceDisabled={withUaePass}
          >
            <DateInput disableDateRule="date > today" />
          </Form.Item>
        </InputsGroupLayout>
      </Section>

      <Section formItemLast>
        <Typography.Title level={4}>{t('UAE residency & contact details').toUpperCase()}</Typography.Title>
        <InputsGroupLayout>
          <Form.Item label={t('Country')} name={n(['country'])} initialValue={features.defaultCountrySalesforce}>
            <CountryInput />
          </Form.Item>
          <PhoneNumberFormItem name={n(['mobileNo'])} forceDisabled={withUaePass} optional />
          <EmailFormItem name={n(['email'])} forceDisabled={withUaePass} />
        </InputsGroupLayout>
      </Section>

      {withProof && (
        <Form.Visible name={n(['isAuthorizedSignatory'])}>
          <Section formItemLast>
            <Typography.Title level={4}>{t('Authorised Signatory Details').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item label={t('Designation')} name={n(authorizedRepresentativeCommonNames.designation)}>
                <Input />
              </Form.Item>
            </InputsGroupLayout>
            <Form.Item label={t('Select the document type')} name={n([proof, 'documentType'])}>
              <Select className="AuthorizedRepresentativeFormItems-DocumentTypeSelect" options={getProofOptions()} />
            </Form.Item>
            <Form.Item hidden initialValue={[]} name={n([proof, 'files'])} />
            {withFiles && (
              <Form.Visible name={n([proof, 'documentType'])} visibleIf={uploadPowerOfAttorney}>
                <Form.Item initialValue={[]} name={n([proof, 'files'])}>
                  <DocumentInput name="photos" variant="default" />
                </Form.Item>
              </Form.Visible>
            )}
          </Section>
        </Form.Visible>
      )}
      {isAgent() && withAgentSendInvitation && (
        <Section>
          <AuthorizedRepresentativeSendInvitation
            form={form}
            getPayload={getSendInvitationPayload(() => {
              const formValues = form.getFieldsValue();
              const person: Person = get(formValues, name) || {};
              return person;
            })}
          />
        </Section>
      )}
      {!!withCustomFields && <CustomFields nFn={n} />}
    </Container>
  );
};

const getSendInvitationPayload =
  (getPerson: () => Person) => (): Partial<AuthorizedRepresentativeSendInvitationPayload> => {
    const { email, phoneNumber, firstName, lastName } = getPerson();
    return { email, phoneNumber, firstName, lastName };
  };

const passport = 'passport';
const emiratesId = 'emiratesId';
const proof = 'proof';

export const authorizedRepresentativeCommonNames = {
  firstName: ['firstName'],
  lastName: ['lastName'],
  passport: [passport],
  emiratesId: [emiratesId],
  designation: [proof, 'designation'],
};

export const uploadPowerOfAttorney = 'Upload a copy of valid notarized Power of Attorney of authorized signatory';
const getProofOptions: any = () => [
  {
    label: i18n.t('Person name is listed as Authorized Signatory on Company license', { ns: 'fillApplication' }),
    value: 'Person name is listed as manager on Company license',
  },
  {
    label: i18n.t(uploadPowerOfAttorney, { ns: 'fillApplication' }),
    value: uploadPowerOfAttorney,
  },
];

const Container = styled.div`
  @media (max-width: ${theme.breakpoints.md}px) {
    .AuthorizedRepresentativeFormItems-DocumentTypeSelect {
      max-width: calc(100vw - ${32 * 2}px);
    }
  }
  @media (max-width: ${theme.breakpoints.sm}px) {
    .AuthorizedRepresentativeFormItems-DocumentTypeSelect {
      max-width: calc(100vw - ${12 * 2 + 12 * 2}px);
    }
  }
`;

export default AuthorizedRepresentativeFormItems;
